import GoogleTagDataLayer from "app/components/GoogleTagDataLayer/GoogleTagDataLayer";
import InfoMessage from "app/components/InfoMessage/InfoMessage";
import { PublicViewInfoMessageTimeout } from "app/utils/const";
import isPublicView from "app/utils/publicViewUtils";
import cx from "classnames";
import dayjs from "dayjs";
import advanced from "dayjs/plugin/advancedFormat";
import localeData from "dayjs/plugin/localeData";
import localizedFormat from "dayjs/plugin/localizedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { SessionHelper } from "../../../../../../helpers/sessionHelper";
import { TimezoneHelper } from "../../../../../../helpers/timezoneHelper";
import SummaryLogic from "../../../../../../logic/summaryLogic";
import Bookmarks from "../../../../../EventsScreen/components/Bookmarks/Bookmarks";
import BookmarksButton from "../components/BookmarksButton/BookmarksButton";
import FavouritesButton from "../components/FavouritesButton/FavouritesButton";
import SearchEventsAndLeagues from "../components/SearchEventsAndLeagues/SearchEventsAndLeagues";
import SearchEventsAndLeaguesTime from "../components/SearchEventsAndLeaguesTime/SearchEventsAndLeaguesTime";
import "./AsianHeaderLayoutDesktop.scss";
import "./AsianHeaderLayoutTablet.scss";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advanced);
dayjs.extend(localizedFormat);
dayjs.extend(localeData);

const formatTimezoneOffset = userTimezone => {
  const offset = dayjs().tz(userTimezone.name).format("zz");
  return isNaN(+offset)
    ? `(${dayjs().tz(userTimezone.name).format("z")})`
    : `(UTC${dayjs().tz(userTimezone.name).format("ZZ")})`;
};

const getCurrentTime = (userTimezone, isUserLoading) => {
  return isUserLoading
    ? ""
    : `${dayjs().tz(userTimezone.name).format("llll")} ${formatTimezoneOffset(
        userTimezone
      )}`;
};

const AsianHeaderLayoutDesktop = ({
  bookmarksTotal,
  bookmarksOnly,
  userTimezone,
  showUnsummarizedMarketsModal,
  favouritesOnly,
  onClickBookmarks,
  onClickFavourites,
  isCompetitionListLoading,
  currentFavouritesCount,
  stopCompetitionsPolling,
  selectLeagues,
  isUserLoading,
  setTimezone,
}) => {
  const [searchActive, setSearchActive] = useState(false);
  const [editingTimezone, setEditingTimezone] = useState(false);
  const [currentTime, setCurrentTime] = useState("");
  const [infoMessageBookmark, setInfoMessageBookmark] = useState(false);
  const [infoMessageFavourites, setInfoMessageFavourites] = useState(false);

  useEffect(() => {
    SummaryLogic.updateSummary();
    const clockIntervalId = setInterval(() => {
      const time = getCurrentTime(userTimezone, isUserLoading);
      if (currentTime !== time) {
        setCurrentTime(time);
      }
    }, 1000);
    return () => clearInterval(clockIntervalId);
  }, [userTimezone, isUserLoading, currentTime]);

  useEffect(() => {
    if (
      SessionHelper.isAsianRouteAndSessionView() &&
      !isUserLoading &&
      userTimezone.name === "GMT"
    ) {
      const userTz = TimezoneHelper.detectUserTimeZone();
      if (userTz && !isPublicView()) {
        setTimezone(userTz);
      }
    }
  }, [userTimezone, isUserLoading, setTimezone]);

  const onClickTimezone = useCallback(event => {
    setEditingTimezone(true);
    window.addEventListener("click", onClickOutsideTimezoneDropdown);
    event.stopPropagation();
  }, []);

  const onClickOutsideTimezoneDropdown = useCallback(event => {
    if (document.getElementById("timezone-dropdown") !== event.target) {
      setEditingTimezone(false);
      window.removeEventListener("click", onClickOutsideTimezoneDropdown);
    }
  }, []);

  const onChangeTimezone = useCallback(
    timezone => {
      const code = TimezoneHelper.getTimezoneCode(timezone);
      if (code) setTimezone(code);
      setEditingTimezone(false);
    },
    [setTimezone]
  );

  const showHelpWidgetForBookmark = useCallback(() => {
    setInfoMessageBookmark(true);
  }, []);

  const hideHelpWidgetForBookmark = useCallback(() => {
    setTimeout(() => {
      setInfoMessageBookmark(false);
    }, PublicViewInfoMessageTimeout.infoMessageTimeout);
  }, []);

  const showHelpWidgetForFavourites = useCallback(() => {
    setInfoMessageFavourites(true);
  }, []);

  const hideHelpWidgetForFavourites = useCallback(() => {
    setTimeout(() => {
      setInfoMessageFavourites(false);
    }, PublicViewInfoMessageTimeout.infoMessageTimeout);
  }, []);

  return (
    <div
      className={cx({
        "events-screen-header__options--desktop": !bookmarksOnly,
        "events-screen-header__options--bookmarks-active": bookmarksOnly,
        suspended: showUnsummarizedMarketsModal,
      })}
    >
      {favouritesOnly && (
        <div className="favourites-header">
          <span data-qa="favourites-header__label__text">
            <FormattedMessage id="events-favorites" />
          </span>
          <span data-qa="favourites-header__label__count">
            &nbsp;({currentFavouritesCount})
          </span>
        </div>
      )}
      <div className="events-screen-header__options__filters">
        <div
          className={cx("search-events-header__filter-by", {
            "search-events-header__filter-by--no-filter": !bookmarksOnly,
          })}
        >
          <GoogleTagDataLayer
            googleTagClickId="asian_bookmark"
            dlvEventName="DLV-bookmark-Asian"
          >
            <span
              className="help-widget__bookmark"
              onClick={() => isPublicView() && showHelpWidgetForBookmark()}
              onPointerLeave={() =>
                isPublicView() && hideHelpWidgetForBookmark()
              }
            >
              {infoMessageBookmark && (
                <InfoMessage message="public-view-login-to-action" />
              )}
              <BookmarksButton
                onClickBookmarks={onClickBookmarks}
                bookmarksOnly={bookmarksOnly}
                favouritesOnly={favouritesOnly}
                bookmarksTotal={bookmarksTotal}
              />
            </span>
          </GoogleTagDataLayer>
          <GoogleTagDataLayer
            googleTagClickId="asian_favourites"
            dlvEventName="DLV-favourites-Asian"
          >
            <span
              className="help-widget__bookmark"
              onClick={() => isPublicView() && showHelpWidgetForFavourites()}
              onPointerLeave={() =>
                isPublicView() && hideHelpWidgetForFavourites()
              }
            >
              {infoMessageFavourites && (
                <InfoMessage
                  customStyle="info-message__favourites"
                  message="public-view-login-to-action"
                />
              )}
              <FavouritesButton
                onClickFavourites={onClickFavourites}
                bookmarksOnly={bookmarksOnly}
                favouritesOnly={favouritesOnly}
                isCompetitionListLoading={isCompetitionListLoading}
                currentFavouritesCount={currentFavouritesCount}
              />
            </span>
          </GoogleTagDataLayer>
        </div>
        <SearchEventsAndLeagues
          onClickSearchActive={() => setSearchActive(!searchActive)}
          searchActive={searchActive}
          bookmarksOnly={bookmarksOnly}
          favouritesOnly={favouritesOnly}
          onClickBookmarks={onClickBookmarks}
          stopCompetitionsPolling={stopCompetitionsPolling}
          selectLeagues={selectLeagues}
        />
      </div>
      <SearchEventsAndLeaguesTime
        bookmarksOnly={bookmarksOnly}
        favouritesOnly={favouritesOnly}
        currentTime={currentTime}
        editingTimezone={editingTimezone}
        onClickTimezone={onClickTimezone}
        onChangeTimezone={onChangeTimezone}
        userTimezone={userTimezone}
      />
      {bookmarksOnly && <Bookmarks />}
    </div>
  );
};

const mapStateToProps = state => ({
  bookmarksTotal: state.bookmarkListModel.total,
  favouritesOnly: state.competitionListModel.favouritesOnly,
  bookmarksOnly: state.asianCompetitionListFiltersModel.bookmarksOnly,
  userTimezone: state.userModel.timezone,
  isUserLoading: state.userModel.isUserLoading,
  showUnsummarizedMarketsModal:
    state.competitionListModel.showUnsummarizedMarketsModal,
});

const mapDispatchToProps = dispatch => ({
  setTimezone: timezone => dispatch.userModel.updateTimezone(timezone),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AsianHeaderLayoutDesktop);
